import type { TimeRange, MediaSelection } from '@mimir/common/util/timeRange';
import type { FrameTime } from '@mimir/client-common/time/frameTime';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { Observable } from '@mimir/common/util/observable';
import type { AudioTrackConfig, AudioTrackConfigUpdate, PeakRange } from './audioTrackConfig';
import type { QualityChangeState, QualitySelectionResult } from './playbackQuality';
import type { PlaybackQuality, QualityOption } from '@mimir/common/types/playbackQuality';
import type { SubtitleCue } from '@mimir/common/types/subtitleCue';
import type { VisualizationMode } from '@mimir/browser/cutter/audioVisualizer';
import type { AudioLevelsData } from '@mimir/cutter/player/cutterCorePlayer';
import type { Cleanable } from '@mimir/common/util/cleanable';
import type { LutChoice } from '@mimir/browser/cutter/mediaPlayer/cutterMediaPlayer';

export type PlayerError = (err: ErrorEvent) => void;

export interface AudioVisualizationState {
  current: VisualizationMode | null,
  available: (VisualizationMode | null)[],
}

/**
 * Audio initialization callbacks when loading peaks
 * either from individual .m4a proxies or from peak files.
 */
export interface AudioInitializationCallbacks {
  onError: (error: any) => void;
  /**
   * Only relevant for .m4a files.
   * Invoked when we start to download .m4a files.
   * Progress is a value between 0.0 and 1.0
   */
  onDowloadProgress: (progress: number) => void;

  /**
   * Only relevant for .m4a files.
   * Invoked when the downloaded .m4a files are being decoded.
   */
  onStartDecoding: () => void;
  /**
   * Invoked when we can start showing peaks.
   */
  showPeaks: () => void;
}

export class NoOpM4aProxiesCallback implements AudioInitializationCallbacks {
  onError(_err: any) {}
  onDowloadProgress(_progress: number) {}
  onStartDecoding() {}
  showPeaks() {}
}

/**
 * A callback that will be invoked when the player is ready
 */
export interface PostLoadCallback {
  type: 'callback';
  callback: () => void;
}

/**
 * Describes the initial position the player should go and whether
 * it should start playing from there
 */
export interface PostLoadSeek {
  type: 'seek';
  positionInMs: number;
  play: boolean;
}

export type PostLoadOperation = PostLoadCallback | PostLoadSeek;

export function removePlayFromPostLoadOperation(postLoadOperation: PostLoadOperation): PostLoadOperation {
  switch (postLoadOperation.type) {
    case 'seek': {
      return {
        type: 'seek',
        play: false,
        positionInMs: postLoadOperation.positionInMs,
      };
    }
    case 'callback': {
      return {
        type: 'callback',
        callback: postLoadOperation.callback,
      };
    }
  }
}

export interface BufferedRange extends TimeRange {
  kind?: 'demuxed' | 'decoded',
}

export interface ItemMediaPlayerOps {
  loadAudio(callbacks: AudioInitializationCallbacks): void;
  getChannelsAsMatrix(): number[][] | null;
  play(ms?: number): void;
  pause(): void;
  setVtt(vttUrl: string | null): void;

  /**
   *  Seek to an absolute offset in the actual source media. I.e this one does
   *  not take virtual clip in/out points into account.
   *
   *  The word clamped is included in the function name because this function
   *  will ensure that we don't seek to a position before the first frame.
   */
  seekToClampedSourceMediaPosition(sourcePos: FuzzyValue): void;

  /**
   *  Seek to the provided virtual clip media position in seconds. The player
   *  will be set to the requested position relative to the start of the
   *  virtual clip. If the loaded item is not a virtual clip then this will be
   *  the same as calling seekToClampedSourceMediaPosition.
   */
  seekToVirtualClipMediaPosition(virtualClipMediaPosition: FuzzyValue): void;

  /**
   *  Should use the same seeking mechanism as seekToClampedSourceMediaPosition
   */
  seekAndGrabImage(sourcePos: FuzzyValue): Promise<string>;

  addPlayerErrorCallback(callback: PlayerError): void;
  removePlayerErrorCallback(callback: PlayerError): void;

  /**
   * Asks the player to load the item with the given ID.
   * If there was already an item loaded in the player, it is cleared first.
   * If the item does not have media yet, the player will poll the item by itself
   * to wait until media is available, so there is no need for external code
   * to poll and call setItemId() again later.
   */
  setItemId(
    itemId: string,
    itemType: 'video' | 'audio',
    flags: {
      useNewBufferingLogic: boolean,
    },
    callbacks?: {
      noPlayableArtifactYet?: () => void,
      postLoadOperation?: PostLoadOperation,
    },
  ): void;

  /**
   *  Clears any media loaded in the player without destroying the player
   */
  clear(): void;

  togglePlayPause(): void;
  setVolume(volume: number): void;
  setMuted(val: boolean): void;

  getVisibleObs(): Observable<boolean>;

  appendVideoElementToElement(parent: Element): void;

  updateAudioTrackConfig(update: AudioTrackConfigUpdate[]): void;
  setPosterImage(src: string): void;
  setSpeedFactor(speedFactor: number): void;
  toggleFullscreen(): void;
  goForward(ms: number): void;
  goBackward(ms: number): void;
  goForwardBySecond(): void;
  goForwardByTenSeconds(): void;
  goBackwardBySecond(): void;
  goBackwardByTenSeconds(): void;
  goFrameBackward(): void;
  goFrameForward(): void;

  /**
   * Returns the number of peak series for a given audio track index,
   * which should correspond to the number of audio channels that
   * are inside the given track.
   */
  getPeakSeriesCount(trackIndex: number): number;

  /**
   * Given an audio track index, the index of one of the track's channels,
   * a viewport width in pixels and a range within the source media duration
   * range return the peaks to draw for a visualization of peaks
   */
  getPeaksForSourceMediaRange(
    trackIndex: number,
    width: number,
    channel: number,
    range: PeakRange
  ): number[];


  /**
   * Get the source media position where the selected sample starts in seconds.
   * For video players, the sample in question is the shown video frame or the
   * video frame the player is in the process of seeking to.
   *
   * When not null, the return value is a source media position. See
   * https://github.com/Mjoll/mimir/wiki/Media-Time-Values for how this relates
   * to other types of media time values.
   *
   * This value does not take the virtual clip in/out into account meaning that
   * for a virtual clip this may return a non-zero value even though the play
   * is at the start of the visible scrubber.
   */
  getSourceMediaPositionObs(): Observable<FuzzyValue | null>

  /**
   * Get the current virtual clip media position of the player in seconds. See
   * https://github.com/Mjoll/mimir/wiki/Media-Time-Values for how this relates
   * to other types of media time values.
   *
   * This is the current position relative to the virtual clip in point. For
   * non-virtual clip this will always be the same as source media position.
   * (See getSourceMediaPositionObs documentation)
   */
  getVirtualClipMediaPositionObs(): Observable<FuzzyValue>;

  /**
   * Get the amount of time between the start of each sample. For video players,
   * this is the duration of one frame, i.e. the inverse of the frame rate
   * (1s/FPS).
   *
   * The return value is a duration expressed in seconds.
   */
  getSampleDuration(): FuzzyValue | null;

  /**
   * Get the source media position where the first sample of the virtual clip
   * starts.
   *
   * This is the value you need to add to a virtual clip media position to
   * convert it to a source media position. And conversely, this is the value
   * you need to subtract from a source media position to convert it to a
   * virtual clip media position.
   */
  getVirtualClipStartObs(): Observable<FuzzyValue | null>;

  /**
   * Get the source media range which contains the virtual clip.
   */
  getVirtualClipSourceMediaRangeObs(): Observable<MediaSelection>;

  // Observable properties


  /**
   *  This returns the current source media duration. I.e the duration of the
   *  media being loaded NOT the duration of the item (if for instance that
   *  is larger)
   */
  getSourceMediaDurationObs(): Observable<FuzzyValue>;

  /**
   *  This returns the virtual clip duration of the loaded item clamped to
   *  the actual media available. It's kid of a contrived example but let's
   *  say you managed to create a virtual clip of a segmented item where the
   *  output has not been transcoded yet. In that case the out point of the
   *  virtual clip out point would be clamped to the duration actually
   *  transcoded.
   *
   *  If this is not a virtual clip then this just returns the current source
   *  media duration
   */
  getVirtualClipDurationObs(): Observable<FuzzyValue>;

  getPausedObs(): Observable<boolean>;
  getSpeedFactorObs(): Observable<number>;
  getHasMediaObs(): Observable<boolean>;
  getLoadingObs(): Observable<boolean>;
  getHasAudioChannelsObs(): Observable<boolean>;
  getVideoAspectRatioObs(): Observable<number | null>;
  getPlayerAspectRatioObs(): Observable<number | null>;
  getSeekingObs(): Observable<boolean>;
  getCurrentFrameTimeObs(): Observable<FrameTime | null>;
  getLastErrorObs(): Observable<ErrorEvent | null>;
  getBufferedObs(): Observable<BufferedRange[]>;

  getHasGrowingMediaObs(): Observable<boolean>;
  getAudioTrackConfigsObs(): Observable<AudioTrackConfig[]>;

  /**
   * The number of available audio peaks.
   */
  getPeakCountObs(): Observable<number>;

  /**
   * Change the playback quality of the current item.
   * Returns a result indicating success or failure with error message.
   */
  changeQuality(
    selectedQuality: PlaybackQuality,
    flags: {
      useNewBufferingLogic: boolean,
    }
  ): Promise<QualitySelectionResult>;

  /**
   * Get the list of available quality options for the current item.
   */
  getAvailableQualities(): QualityOption[];

  /**
   * Get the current playback quality.
   */
  getCurrentQuality(): PlaybackQuality | null;

  /**
   * Observable for quality change state (loading, error, etc).
   */
  getQualityChangeStateObs(): Observable<QualityChangeState>;

  /**
   *  Permanently destroys the player and cleans up its resources. The player
   *  should not be used after this has been called.
   */
  destroy(): void;

  getCurrentSubtitleCueObs(): Observable<SubtitleCue | null> | null;

  getAudioVisualizationStateObs(): Observable<AudioVisualizationState | null>;

  setAudioVisualization(mode: VisualizationMode | null): void;

  getAudioLevelsDataObs(): Observable<AudioLevelsData | null>;

  /**
   * Enter virtual clip edit mode. The player expands to show the full
   * master clip media so the user can adjust in/out points. The current
   * in/out points are preserved as mark positions on the timeline.
   */
  enterVirtualClipEditMode(): void;

  /**
   * Exit virtual clip edit mode. When newOffsets is provided (after a
   * successful save), the player applies the new in/out points instead
   * of restoring the previous ones.
   */
  exitVirtualClipEditMode(newOffsets: { inPoint: number; outPoint: number } | null): void;

  /**
   * Observable for whether the player is currently in virtual clip edit mode.
   */
  getEditingVirtualClipObs(): Observable<boolean>;

  setEnabledLuts(id: string[]): void;

  getCurrentLutObs(): Observable<string | null>;

  getAvailableLutsObs(): Observable<LutChoice[]>;
}

// TODO: implement itemdetailsplayer to be injected as the quickpreview player is and remove this stub.
/**
 * This is a stub implementation of ItemMediaPlayerOps that can be used before the real players are initialized.
 * It returns default values and has no-op implementations for all functions. This allows the UI to function
 * without errors even if the real media players haven't been set up yet.
 * The returned object should be replaced with a real ItemMediaPlayerOps implementation once the necessary
 * services and configurations are available.
 */
export function createItemMediaPlayerOpsStub(): ItemMediaPlayerOps {

  const observableStub = <T>(initialValue: T): Observable<T> => {
    return {
      observe: (): Cleanable => {
        return {
          cleanup: () => { /* no-op */ },
        };
      },
      currentValue: () => initialValue,
      waitForChange: async () => {
        return new Promise<T>(() => {});
      },
    };
  };

  const zeroFuzzyValue: FuzzyValue = { approximate: 0 };

  const emptyMediaSelection: MediaSelection = {
    whole: true,
    bounds: {
      start: zeroFuzzyValue,
      end: zeroFuzzyValue,
    },
  };

  const playerOpsStub: ItemMediaPlayerOps = {
    loadAudio: () => { /* no-op */ },
    getChannelsAsMatrix: () => null,
    play: () => { /* no-op */ },
    pause: () => { /* no-op */ },
    setVtt: () => { /* no-op */ },
    seekToClampedSourceMediaPosition: () => { /* no-op */ },
    seekToVirtualClipMediaPosition: () => { /* no-op */ },
    seekAndGrabImage: async () => '',
    addPlayerErrorCallback: () => { /* no-op */ },
    removePlayerErrorCallback: () => { /* no-op */ },
    setItemId: () => { /* no-op */ },
    clear: () => { /* no-op */ },
    togglePlayPause: () => { /* no-op */ },
    setVolume: () => { /* no-op */ },
    setMuted: () => { /* no-op */ },
    getVisibleObs: () => observableStub(true),
    appendVideoElementToElement: () => { /* no-op */ },
    updateAudioTrackConfig: () => { /* no-op */ },
    setPosterImage: () => { /* no-op */ },
    setSpeedFactor: () => { /* no-op */ },
    toggleFullscreen: () => { /* no-op */ },
    goForward: () => { /* no-op */ },
    goBackward: () => { /* no-op */ },
    goForwardBySecond: () => { /* no-op */ },
    goForwardByTenSeconds: () => { /* no-op */ },
    goBackwardBySecond: () => { /* no-op */ },
    goBackwardByTenSeconds: () => { /* no-op */ },
    goFrameBackward: () => { /* no-op */ },
    goFrameForward: () => { /* no-op */ },
    getPeakSeriesCount: () => 0,
    getPeaksForSourceMediaRange: () => [],
    getSourceMediaPositionObs: () => observableStub<FuzzyValue | null>(null),
    getVirtualClipMediaPositionObs: () => observableStub(zeroFuzzyValue),
    getSampleDuration: () => null,
    getVirtualClipStartObs: () => observableStub<FuzzyValue | null>(null),
    getVirtualClipSourceMediaRangeObs: () => observableStub(emptyMediaSelection),
    getSourceMediaDurationObs: () => observableStub(zeroFuzzyValue),
    getVirtualClipDurationObs: () => observableStub(zeroFuzzyValue),
    getPausedObs: () => observableStub(true),
    getSpeedFactorObs: () => observableStub(1),
    getHasMediaObs: () => observableStub(false),
    getLoadingObs: () => observableStub(false),
    getHasAudioChannelsObs: () => observableStub(false),
    getVideoAspectRatioObs: () => observableStub<number | null>(null),
    getPlayerAspectRatioObs: () => observableStub<number | null>(null),
    getSeekingObs: () => observableStub(false),
    getCurrentFrameTimeObs: () => observableStub<FrameTime | null>(null),
    getLastErrorObs: () => observableStub<ErrorEvent | null>(null),
    getBufferedObs: () => observableStub<BufferedRange[]>([]),
    getHasGrowingMediaObs: () => observableStub(false),
    getAudioTrackConfigsObs: () => observableStub<AudioTrackConfig[]>([]),
    getPeakCountObs: () => observableStub(0),
    setEnabledLuts: () => { },
    destroy: () => { /* no-op */ },
    changeQuality: async () => ({
      success: false,
      quality: 'web-proxy-flat',
      error: 'This is a stub implementation, changeQuality is not supported',
    }),
    getAvailableQualities: () => [],
    getCurrentQuality: () => null,
    getQualityChangeStateObs: () => observableStub<QualityChangeState>({ inProgress: false }),
    getCurrentSubtitleCueObs: () => null,
    getAudioVisualizationStateObs: () => observableStub<AudioVisualizationState | null>(null),
    setAudioVisualization: () => { /* no-op */ },
    getAudioLevelsDataObs: () => observableStub<AudioLevelsData | null>(null),
    enterVirtualClipEditMode: () => { /* no-op */ },
    exitVirtualClipEditMode: () => { /* no-op */ },
    getEditingVirtualClipObs: () => observableStub(false),
    getCurrentLutObs: () => observableStub(null),
    getAvailableLutsObs: () => observableStub([]),
  };

  return playerOpsStub;
}
